import type {
  PeopleTravelEvent,
  PeopleTravelEventKind,
  PeopleTravelLeg,
  PeopleTravelLegMode,
  PeopleTravelLocation,
  PeopleTravelOption,
} from '../types';

export type TravelCanvasSelection = {
  locationId?: string;
  eventId?: string;
  legId?: string;
};

export const CLUSTER_WIDTH = 280;
export const CLUSTER_MIN_HEIGHT = 168;
export const CLUSTER_PAD_TOP = 44;
export const EVENT_CARD_WIDTH = 248;
export const EVENT_CARD_HEIGHT = 78;
export const EVENT_GAP = 10;
export const LOCATION_X_GAP = 420;
export const LOCATION_ORIGIN_X = 80;
export const LOCATION_ORIGIN_Y = 80;

export const EVENT_KIND_LABELS: Record<PeopleTravelEventKind, string> = {
  work: 'Site / work',
  meeting: 'Meeting',
  hotel: 'Hotel',
  meal: 'Meal',
  other: 'Event',
};

export const LEG_MODE_LABELS: Record<PeopleTravelLegMode, string> = {
  flight: 'Fly',
  drive: 'Drive',
  rental_car: 'Rental',
  other: 'Travel',
};

export function defaultEventPosition(index: number): { x: number; y: number } {
  return {
    x: 16,
    y: CLUSTER_PAD_TOP + index * (EVENT_CARD_HEIGHT + EVENT_GAP),
  };
}

export function clusterSize(eventCount: number): { width: number; height: number } {
  const rows = Math.max(eventCount, 1);
  return {
    width: CLUSTER_WIDTH,
    height: Math.max(
      CLUSTER_MIN_HEIGHT,
      CLUSTER_PAD_TOP + rows * (EVENT_CARD_HEIGHT + EVENT_GAP) + 16,
    ),
  };
}

export function nextLocationSlot(existing: PeopleTravelLocation[]): {
  x: number;
  y: number;
  sortOrder: number;
} {
  const sortOrder = existing.length
    ? Math.max(...existing.map(l => l.sortOrder)) + 1
    : 0;
  return {
    x: LOCATION_ORIGIN_X + existing.length * LOCATION_X_GAP,
    y: LOCATION_ORIGIN_Y,
    sortOrder,
  };
}

export function inferOriginLabel(options: PeopleTravelOption[]): string {
  for (const opt of options) {
    if (opt.kind !== 'flight') continue;
    const details = opt.details || {};
    const airport =
      (typeof details.departureAirport === 'string' && details.departureAirport) ||
      (typeof details.origin === 'string' && details.origin) ||
      (typeof details.departureCity === 'string' && details.departureCity);
    if (airport) return String(airport).trim();
  }
  return 'Home';
}

export function searchKindsForSelection(
  selection: TravelCanvasSelection | null,
  legs: PeopleTravelLeg[],
  events: PeopleTravelEvent[],
): Array<'flight' | 'hotel' | 'rental_car'> {
  if (selection?.legId) {
    const leg = legs.find(l => l.id === selection.legId);
    if (leg?.mode === 'flight') return ['flight'];
    if (leg?.mode === 'rental_car' || leg?.mode === 'drive') return ['rental_car'];
    return ['flight', 'rental_car'];
  }
  if (selection?.eventId) {
    const event = events.find(e => e.id === selection.eventId);
    if (event?.kind === 'hotel') return ['hotel'];
  }
  return ['flight', 'hotel', 'rental_car'];
}

export function formatCanvasSelectionLabel(
  selection: TravelCanvasSelection | null,
  locations: PeopleTravelLocation[],
  events: PeopleTravelEvent[],
  legs: PeopleTravelLeg[],
): string {
  if (!selection) return 'Whole trip';
  if (selection.eventId) {
    const event = events.find(e => e.id === selection.eventId);
    const loc = locations.find(l => l.id === event?.locationId);
    return event
      ? `${EVENT_KIND_LABELS[event.kind]} · ${event.title}${loc ? ` @ ${loc.label}` : ''}`
      : 'Selected event';
  }
  if (selection.legId) {
    const leg = legs.find(l => l.id === selection.legId);
    const from = locations.find(l => l.id === leg?.fromLocationId);
    const to = locations.find(l => l.id === leg?.toLocationId);
    return leg
      ? `${LEG_MODE_LABELS[leg.mode]} ${from?.label || '?'} → ${to?.label || '?'}`
      : 'Selected leg';
  }
  if (selection.locationId) {
    const loc = locations.find(l => l.id === selection.locationId);
    return loc ? `Location · ${loc.label}` : 'Selected location';
  }
  return 'Whole trip';
}

export function travelCanvasSnapshotJson(args: {
  locations: PeopleTravelLocation[];
  events: PeopleTravelEvent[];
  legs: PeopleTravelLeg[];
  selection?: TravelCanvasSelection | null;
}): string {
  return JSON.stringify(
    {
      selected: args.selection || null,
      locations: args.locations.map(l => ({
        id: l.id,
        label: l.label,
        lat: l.lat,
        lng: l.lng,
        sortOrder: l.sortOrder,
        startDate: l.startDate,
        endDate: l.endDate,
      })),
      events: args.events.map(e => ({
        id: e.id,
        locationId: e.locationId,
        title: e.title,
        kind: e.kind,
        startAt: e.startAt,
        endAt: e.endAt,
        notes: e.notes,
      })),
      legs: args.legs.map(l => ({
        id: l.id,
        fromLocationId: l.fromLocationId,
        toLocationId: l.toLocationId,
        mode: l.mode,
        notes: l.notes,
        selectedOptionIds: l.selectedOptionIds,
      })),
    },
    null,
    2,
  );
}

export function optionsForLeg(
  options: PeopleTravelOption[],
  leg: PeopleTravelLeg,
): PeopleTravelOption[] {
  const selectedIds = new Set(leg.selectedOptionIds || []);
  return options.filter(
    o => o.legId === leg.id || selectedIds.has(o.id),
  );
}

export function optionsForEvent(
  options: PeopleTravelOption[],
  event: PeopleTravelEvent,
): PeopleTravelOption[] {
  return options.filter(o => o.eventId === event.id);
}
